// src/ActionPlan.js
import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  SmartToyOutlined as BotIcon,
  Save as SaveIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  TuneSharp as TuneIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Paper, Button, IconButton, Badge, Snackbar, Alert, Tooltip, Chip, Tabs, Tab, Dialog, DialogTitle, DialogActions, DialogContent, Popover
} from '@mui/material';

import ControlEvaluationForm from './ControlEvaluation';
import IconWithProgress from './IconWithProgress';
import {
  setSavedControls, fetchSavedControlEvaluations,
  fetchActionPlan, 
  editGeneratedActionPlanRow, editSavedActionPlanRow,
  discardControl, discardSavedControl,  saveControl
} from './redux/actions/controlActions';

import RiskMitigationPlan  from './RiskMitigationPlan';
import AutosaveIndicator   from './AutoSaveIndicator';
import useSaveOperation    from './useSaveOperation';

const FloatingGeneratedButton = ({ count, onClick, isPulse }) => (
  <Box sx={{
    backgroundColor:'#FFFFFF',
    boxShadow:3,
    borderRadius:'50%',
    p:0.5,
    transform: isPulse ? 'scale(1.2)' : 'scale(1)',
    transition: 'transform 0.3s ease'
  }}>
    <Tooltip title="Show generated actions" arrow>
      <Badge badgeContent={count} color="primary">
        <IconButton onClick={onClick}>
          <BotIcon />
        </IconButton>
      </Badge>
    </Tooltip>
  </Box>
);

const FloatingSavedButton = ({ count, onClick, isPulse }) => (
  <Box sx={{
    backgroundColor:'#FFFFFF',
    boxShadow:3,
    borderRadius:'50%',
    p:0.5,
    transform: isPulse ? 'scale(1.2)' : 'scale(1)',
    transition: 'transform 0.3s ease'
  }}>
    <Tooltip title="Show saved actions" arrow>
      <Badge badgeContent={count} color="primary">
        <IconButton onClick={onClick}>
          <SaveIcon />
        </IconButton>
      </Badge>
    </Tooltip>
  </Box>
);

const FloatingTrashButton = ({ trash, onClick, isPulse }) => (
  <Box sx={{
    backgroundColor:'#FFFFFF',
    boxShadow:3,
    borderRadius:'50%',
    p:0.5,
    transform: isPulse ? 'scale(1.2)' : 'scale(1)',
    transition: 'transform 0.3s ease'
  }}>
    <Tooltip title="Discarded actions" arrow>
      <Badge badgeContent={trash} color="error">
        <IconButton onClick={onClick}><DeleteIcon/></IconButton>
      </Badge>
    </Tooltip>
  </Box>
);

/* ───────────────── trash modal ───────────────── */

function TrashModal({ open, onClose, items }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Discarded actions</DialogTitle>
      <DialogContent dividers>
        {items.length ? (
          items.map(c => (
            <Typography key={c.controlId} sx={{ mb:1 }}>• {c.name}</Typography>
          ))
        ) : (
          <Typography>No items in trash.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon/>}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
const riskPriorityColors = {
    Critical:    '#1A1A1A',
    High:        '#993333',
    Significant: '#B94D4D',
    Moderate:    '#D4AA7D',
    Low:         '#B8C2B7',
    Negligible:  '#D8D8D8',
    None:        '#EBEBEB',
  };
/* ───────────────── component ───────────────── */

function ActionPlan({
   riskFormData,           // entire assessment state for this model
   modelId,
  assessmentUuid
}) {
  const dispatch             = useDispatch();
  const { isSaving, isSaved, handleSaveOperation} = useSaveOperation();

  /* ----- global redux slices ----- */
  const actionPlan        = useSelector(s=>s.controls.actionPlan);
  const savedControls  = useSelector(s=>s.controls.savedControls);
  const discardedObj      = useSelector(s=>s.controls.discardedControls);
  const savedEvaluations  = useSelector(s=>s.controls.savedControlEvaluations);
  const isLoading         = useSelector(s=>s.controls.loading);      // ← used for button spinner
  const [showTrash, setShowTrash] = useState(false);   // NEW

  // Animation triggers for floating buttons
  const [triggerGeneratePulse, setTriggerGeneratePulse] = useState(false);
  const [triggerSavePulse, setTriggerSavePulse] = useState(false);
  const [triggerTrashPulse, setTriggerTrashPulse] = useState(false);

  const triggerGenerateAnimation = () => {
    setTriggerGeneratePulse(true);
    setTimeout(() => setTriggerGeneratePulse(false), 500);
  };
  const triggerSaveAnimation = () => {
    setTriggerSavePulse(true);
    setTimeout(() => setTriggerSavePulse(false), 500);
  };
  const triggerTrashAnimation = () => {
    setTriggerTrashPulse(true);
    setTimeout(() => setTriggerTrashPulse(false), 500);
  };


  /* ----- rows filtered to this model ----- */
  const rows = useMemo(
    () => actionPlan.filter(c => c.modelId === modelId),
    [actionPlan, modelId]
  );

  const savedSet = useMemo(
    () => new Set(rows.filter(r => r.status==='saved').map(r=>r.controlId)),
    [rows]
  );

  const generatedCount = rows.filter(r => r.status==='generated').length;
  const savedCount     = rows.filter(r => r.status==='saved').length;
  const trashCount     = Object.values(discardedObj)
                          .flat().filter(c=>c.modelId===modelId).length;

  /* ----- local UI state ----- */
  // Set initial view: if any saved controls, show 'saved', else default to 'generated'
  const [view, setView] = useState(() => savedCount > 0 ? 'saved' : 'generated');
  // Auto-switch view when savedCount or generatedCount change
  useEffect(() => {
    // switch only when the *current* view has become empty
    if (view === 'saved' && savedCount === 0 && generatedCount > 0) {
      setView('generated');
    } else if (view === 'generated' && generatedCount === 0 && savedCount > 0) {
      setView('saved');
    }
  }, [savedCount, generatedCount, view]);
  const [editId,setEditId]      = useState(null);
  const [editTxt,setEditTxt]    = useState('');
  const [snack,setSnack]        = useState(null);
  const [activeRisk,setActive]  = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const openPopover  = e => setAnchorEl(e.currentTarget);
  const closePopover = () => setAnchorEl(null);
  /* bootstrap saved controls / evals once */
  useEffect(()=>{
    if(modelId && assessmentUuid){
      dispatch(setSavedControls(modelId,assessmentUuid));
      dispatch(fetchSavedControlEvaluations(modelId,assessmentUuid));
    }
  },[modelId,assessmentUuid,dispatch]);

  /* flat list of all risks (for chip labels) */
  const allRisks = useMemo(
    ()=> Object.values(riskFormData)
          .flatMap(sec=>Object.values(sec.sectionRisks||{}))
          .flat(),
    [riskFormData]
  );
  const findRisk = rid => allRisks.find(r=>r.riskId===rid);

  /* ───────── Coverage memo for “generated” rows ───────── */
  const generatedRows = useMemo(
    () => rows.filter(r => r.status === 'generated'),
    [rows]
  );

  const coverage = useMemo(() => {
    const totalRisks   = allRisks.length;
    const mitigatedSet = new Set(
      generatedRows.flatMap(c => c.riskIds || [])
    );
    return {
      total      : totalRisks,
      mitigated  : mitigatedSet.size,
      uncovered  : allRisks
                    .filter(r => !mitigatedSet.has(r.riskId)),
      coveredMap : mitigatedSet      // for fast look-up in pop-over
    };
  }, [allRisks, generatedRows]);

  /* ---------- handlers ---------- */
  const confirmEdit = (ctrl,newName,isSavedRow)=>{
    if(isSavedRow){
      dispatch(editSavedActionPlanRow(ctrl.controlId,newName));
    }else{
      dispatch(editGeneratedActionPlanRow(ctrl.controlId,newName));
    }
    setSnack({txt:'Name updated',sev:'success'});
  };
  const titleCase = str => str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';
  // const handleSave = ctrl=>{
  //   dispatch(addSavedControl({...ctrl,status:'saved'}));
  // };
  const handleDiscard = (ctrl,isSavedRow)=>{
    if(isSavedRow){
      dispatch(discardSavedControl(ctrl,ctrl.riskId||ctrl.riskIds[0]));
    }else{
      dispatch(discardControl(ctrl,ctrl.riskId||ctrl.riskIds[0]));
    }
  };
  const onSaveControlClick = async (risk, control) => {
    try {
      // wraps the thunk in your existing loading/saved UI handler
      await handleSaveOperation(() =>
        dispatch(saveControl({
          risk,
          control,
          modelId,
          assessmentUuid,
          controlModelType: null,    // or pass your actual values
          controlModelStage: null,
        }))
      );
      setSnack({ txt: 'Control saved successfully!', sev: 'success' });
          } catch (error) {
      setSnack({ txt: error.message || 'Error saving control', sev: 'error' });
    }
  };

  /* ---------- generate / refresh global action-plan ---------- */
    const runFetchActionPlan = () => {
      dispatch(fetchActionPlan({
        riskFormData,
        modelId,
        assessmentUuid,
        controlModelType: null,
        controlModelStage: null,
        savedControls: Object.values(savedControls).flat()
      }));
      setView('generated');
    };
  // keep track of which rows are currently expanded
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedTabs, setExpandedTabs] = useState({});

  // open/close a row and optionally jump to a specific tab
  const toggleRow = (id, targetTab) => {
    // ── 1. ensure the row is expanded ─────────────────────
    setExpandedRows(prev => {
      const next = new Set(prev);

      // collapse only if it's already open *and* no tab change is requested
      if (next.has(id) && !targetTab) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

    // ── 2. switch the tab if requested ────────────────────
    if (targetTab) {
      setExpandedTabs(prev => ({ ...prev, [id]: targetTab }));
    }
  };

  /* ---------- render ---------- */
  return (
    <>
      <Box px={3} py={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center">
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: '#374151', // Slightly lighter than main titles
              fontSize: '1rem', // 16px - more appropriate for section headers
              letterSpacing: '-0.01em',
              lineHeight: 1.4,
              ml:1
            }}
          >Risk Handling Plan</Typography>
            <AutosaveIndicator isSaving={isSaving} isSaved={isSaved}/>
            {generatedCount > 0 && (
             <Tooltip title="Hover to see by-risk breakdown" arrow>
               <Box
                 onMouseEnter={openPopover}
                 onMouseLeave={closePopover}
                 sx={{
                   ml: 1,
                   px: 1.25,
                   borderRadius: 1,
                   fontSize: 13,
                   fontWeight: 500,
                   backgroundColor: coverage.mitigated === coverage.total
                     ? 'success.light'
                     : 'warning.light',
                   color: coverage.mitigated === coverage.total
                     ? 'success.dark'
                     : 'warning.dark',
                   cursor:'default',
                   display:'flex',
                   alignItems:'center',
                   gap:0.5
                 }}
               >
                 {coverage.mitigated}/{coverage.total}
                 {coverage.mitigated === coverage.total
                   ? <CheckIcon fontSize="inherit"/>
                   : <InfoIcon  fontSize="inherit"/>}
               </Box>
             </Tooltip>
           )}
          </Box>
        </Box>

     
        {/* floating buttons */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Button
            variant="contained"
            size="small"
            startIcon={
              isLoading
                ? <IconWithProgress isLoading tooltip="Generating action plan"/>
                : <BotIcon/>
            }
            disabled={isLoading}
            onClick={runFetchActionPlan}
            sx={{
              height: 48,
              backgroundColor:'#111827',
              color:'#FFFFFF',
              '&:hover':{ backgroundColor:'#1F2937' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              px: 2.5
            }}
          >
            {generatedCount ? 'Refresh Action-Plan' : 'Generate Action-Plan'}
          </Button>
          <Box display="flex" alignItems="center" gap={2}>
            <FloatingGeneratedButton
              count={generatedCount}
              isPulse={triggerGeneratePulse}
              onClick={() => {
                triggerGenerateAnimation();
                setView('generated');
              }}
            />
            <FloatingSavedButton
              count={savedCount}
              isPulse={triggerSavePulse}
              onClick={() => {
                triggerSaveAnimation();
                setView('saved');
              }}
            />
            {/* trash */}
            <FloatingTrashButton
              trash={trashCount}
              isPulse={triggerTrashPulse}
              onClick={() => {
                triggerTrashAnimation();
                setShowTrash(true);
              }}
            />
          </Box>
        </Box>

        {/* table */}
        <TableContainer component={Paper} sx={{ my:2 }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: '#F9FAFB', height: 56 }}>
              <TableRow>
                <TableCell>Mitigation</TableCell>
                <TableCell>Evaluated?</TableCell>
                <TableCell>Mitigates Risks</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.filter(r=>r.status===view).length===0 && (
                <TableRow><TableCell colSpan={5} align="center">
                  No {view} actions yet.
                </TableCell></TableRow>
              )}

              {rows.filter(r=>r.status===view).map(ctrl=>{
                const evalRow = ctrl.riskIds?.[0]
                  ? (savedEvaluations[ctrl.riskIds[0]]?.[ctrl.controlId]?.[0]||{})
                  : {};
                const evaluated = evalRow.control_recurrence||evalRow.executed_next||evalRow.self_evaluation;
                const isSaved   = savedSet.has(ctrl.controlId);

                return (
                  <React.Fragment key={ctrl.controlId}>
                    <TableRow
                      hover
                      onClick={() => toggleRow(ctrl.controlId)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {/* Mitigation */}
                      <TableCell sx={{ maxWidth:500 }}>
                        {editId===ctrl.controlId
                          ? (
                              <Box display="flex" gap={1}>
                                <input
                                  style={{flex:1}}
                                  value={editTxt}
                                  onChange={e=>setEditTxt(e.target.value)}
                                />
                                <IconButton size="small" onClick={e=>{
                                  e.stopPropagation();
                                  confirmEdit(ctrl,editTxt.trim(),isSaved);
                                  setEditId(null);
                                }}><CheckIcon/></IconButton>
                                <IconButton size="small" onClick={e=>{
                                  e.stopPropagation();
                                  setEditId(null);
                                }}>
                                  <CloseIcon fontSize="small"/>
                                </IconButton>
                              </Box>
                            )
                          : ctrl.name}
                      </TableCell>

                      {/* evaluated / tune */}
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            const alreadyOpen   = expandedRows.has(ctrl.controlId);
                            const onEvalTab     = (expandedTabs[ctrl.controlId] ?? 'risks') === 'evaluation';

                            // If row already showing evaluation → collapse; else open evaluation tab
                            if (alreadyOpen && onEvalTab) {
                              toggleRow(ctrl.controlId);            // collapse
                            } else {
                              toggleRow(ctrl.controlId, 'evaluation');
                            }
                          }}
                        >
                          {evaluated
                            ? <CheckCircleIcon color="success" fontSize="small" />
                            : <TuneIcon fontSize="small" />
                          }
                        </IconButton>
                      </TableCell>

                      {/* number of risks – link style & own click‑handler */}
                      <TableCell
                        onClick={e => {
                          e.stopPropagation();
                          // open “risks” tab or collapse if already there
                          if (
                            expandedRows.has(ctrl.controlId) &&
                            (expandedTabs[ctrl.controlId] ?? 'risks') === 'risks'
                          ) {
                            toggleRow(ctrl.controlId);              // collapse
                          } else {
                            toggleRow(ctrl.controlId, 'risks');      // open + show risks tab
                          }
                        }}
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontWeight: 500,
                        }}
                      >
                        {ctrl.riskIds.length}
                      </TableCell>

                      {/* Actions: edit/save/discard */}
                      <TableCell>
                        <IconButton size="small" onClick={e=>{
                          e.stopPropagation();
                          setEditId(ctrl.controlId); setEditTxt(ctrl.name);
                        }}><EditIcon/></IconButton>

                        <IconButton size="small" onClick={e=>{
                          e.stopPropagation();
                          handleDiscard(ctrl,isSaved);
                        }}>
                          <DeleteIcon sx={{ color:'red' }}/>
                        </IconButton>

                        {!isSaved && (
                          <IconButton
                            size="small"
                            onClick={e => {
                              e.stopPropagation();
                              // first risk object (needed by the thunk)
                              const primaryRisk = findRisk(ctrl.riskIds?.[0]);
                              if (primaryRisk) {
                                onSaveControlClick(primaryRisk, ctrl);
                              }
                            }}
                          >
                            <SaveIcon/>
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                    {expandedRows.has(ctrl.controlId) && (
                      <TableRow key={`${ctrl.controlId}-expanded`}>
                        <TableCell colSpan={4} sx={{ pl: 4, bgcolor: '#F9FAFB' }}>
                          {/* -------- Tabs -------- */}
                          <Tabs
                            value={expandedTabs[ctrl.controlId] || 'risks'}
                            onChange={(e, val) =>
                              setExpandedTabs(prev => ({ ...prev, [ctrl.controlId]: val }))
                            }
                            sx={{ mb: 1 }}
                          >
                            <Tab label="Evaluation"  value="evaluation" />
                            <Tab label="Risks"       value="risks" />
                          </Tabs>

                          {/* -------- Tab panels -------- */}
                          { (expandedTabs[ctrl.controlId] || 'risks') === 'risks' ? (
                            /* ---- RISK TABLE (original view) ---- */
                            <TableContainer component={Paper} variant="outlined">
                              <Table size="small">
                                <TableHead sx={{ backgroundColor: '#F9FAFB', height: 44 }}>
                                  <TableRow>
                                    <TableCell>Risk</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell>Priority</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {ctrl.riskIds.map(rid => {
                                    const r = findRisk(rid) || {};
                                    return (
                                      <TableRow key={rid}>
                                        <TableCell>{r.riskName || rid}</TableCell>
                                        <TableCell>{r.category || '—'}</TableCell>
                                        <TableCell>
                                          <Chip
                                            size="small"
                                            label={titleCase(r.priority)}
                                            sx={{
                                              backgroundColor:
                                                riskPriorityColors[titleCase(r.priority)] ||
                                                riskPriorityColors.None,
                                              color: '#fff'
                                            }}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            /* ---- CONTROL-EVALUATION FORM ---- */
                            <Box sx={{ mt: 1 }}>
                              <ControlEvaluationForm
                                riskId={ctrl.riskIds[0]}
                                controlId={ctrl.controlId}
                                modelId={modelId}
                                assessmentUuid={assessmentUuid}
                              />
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* deep-dive drawer */}
      {activeRisk && (
        <RiskMitigationPlan
          open
          onClose={()=>setActive(null)}
          risk={activeRisk}
          modelId={modelId}
          assessmentUuid={assessmentUuid}
        />
      )}
      
      <TrashModal
          open={showTrash}
          onClose={() => setShowTrash(false)}
          items={Object.values(discardedObj).flat().filter(c => c.modelId===modelId)}
        />
        <Popover
       open={Boolean(anchorEl)}
       anchorEl={anchorEl}
       onClose={closePopover}
       anchorOrigin={{ vertical:'bottom', horizontal:'left' }}
       transformOrigin={{ vertical:'top', horizontal:'left' }}
       PaperProps={{ sx:{ p:1, maxHeight:300, overflowY:'auto' } }}
       disableRestoreFocus
     >
       <Table size="small" sx={{ minWidth:300 }}>
         <TableHead>
           <TableRow>
             <TableCell sx={{ fontWeight:600 }}>Risk</TableCell>
             <TableCell sx={{ fontWeight:600, width:64 }} align="center">✓</TableCell>
           </TableRow>
         </TableHead>
         <TableBody>
           {allRisks.map(r => {
             const covered = coverage.coveredMap.has(r.riskId);
             return (
               <TableRow key={r.riskId}>
                 <TableCell>{r.riskName}</TableCell>
                 <TableCell align="center">
                   {covered
                     ? <CheckCircleIcon fontSize="small" color="success"/>
                     : <CloseIcon       fontSize="small" color="disabled"/>}
                 </TableCell>
               </TableRow>
             );
           })}
         </TableBody>
       </Table>
     </Popover>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={()=>setSnack(null)}
        anchorOrigin={{vertical:'bottom',horizontal:'center'}}
      >
        {snack && <Alert severity={snack.sev}>{snack.txt}</Alert>}
      </Snackbar>
    </>
  );
}

export default ActionPlan;

// // src/ActionPlan.js

// import React, { useState, useEffect, useMemo } from 'react';
// import { useSelector, useDispatch } from 'react-redux';

// // MUI + Icons
// import {
//   ExpandLess as ExpandLessIcon,
//   Edit as EditIcon,
//   Check as CheckIcon,
//   Save as SaveIcon,
//   ListAltOutlined as ListAltOutlinedIcon,
//   DeleteOutline as DeleteIcon,
//   Restore as RestoreIcon,
//   Close as CloseIcon,
//   SmartToyOutlined as SmartToyOutlinedIcon,
//   TuneSharp as TuneSharpIcon,
//   CheckCircle as CheckCircleIcon,
//   ExpandMore as ExpandMoreIcon
// } from '@mui/icons-material';
// import {
//   Box,
//   Button,
//   Typography,
//   Badge,
//   Table,
//   TableContainer,
//   TableHead,
//   TableBody,
//   TableRow,
//   TableCell,
//   Divider,
//   Drawer,
//   Collapse,
//   Chip,
//   IconButton,
//   TextField,
//   Snackbar,
//   Alert,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   List,
//   ListItem,
//   ListItemText,
//   ListItemSecondaryAction,
//   Tooltip,
//   Fade,
//   FormControlLabel,
//   Switch
// } from '@mui/material';

// // DnDKit
// import {
//   DndContext,
//   useDroppable,
//   useDraggable,
//   useSensor,
//   useSensors,
//   PointerSensor,
//   closestCenter,
//   DragOverlay,
// } from '@dnd-kit/core';
// import { restrictToWindowEdges } from '@dnd-kit/modifiers';
// import { CSS } from '@dnd-kit/utilities';

// // Redux Actions
// import {
//   fetchControls,
//   setSavedControls,
//   fetchSavedControlEvaluations,
//   addSavedControl,
//   discardControl,
//   discardSavedControl,
//   editGeneratedControl,
//   editSavedControl,
//   editSavedActionPlanRow,
//   editGeneratedActionPlanRow,
//   fetchActionPlan
// } from './redux/actions/controlActions';

// // Helpers
// import IconWithProgress from './IconWithProgress';
// import AutosaveIndicator from './AutoSaveIndicator';
// import useSaveOperation from './useSaveOperation';
// import ControlEvaluationForm from './ControlEvaluation';

// /** =========================
//  *   Floating Trash Button
//  ========================= **/
// const FloatingTrashButton = ({ trashCount, onClick }) => {
//   const { isOver, setNodeRef } = useDroppable({ id: 'discardedControls' });

//   return (
//     <Tooltip title="View discarded controls" arrow placement="left">
//       <Badge
//         ref={setNodeRef}
//         badgeContent={trashCount}
//         color="error"
//         max={99}
//         overlap="circular"
//         sx={{
//           '& .MuiBadge-badge': {
//             fontSize: '0.75rem',
//             height: '22px',
//             minWidth: '22px',
//             padding: '0 4px',
//             top: '0px',
//             right: '-5px',
//           },
//         }}
//       >
//         <IconButton
//           onClick={onClick}
//           sx={{
//             backgroundColor: isOver ? 'error.light' : 'background.paper',
//             boxShadow: 3,
//             '&:hover': {
//               backgroundColor: 'action.hover',
//               transform: 'scale(1.1)',
//             },
//             transition: 'transform 0.2s ease-in-out',
//           }}
//         >
//           <DeleteIcon />
//         </IconButton>
//       </Badge>
//     </Tooltip>
//   );
// };

// /** =========================
//  *   Floating Save Button
//  ========================= **/

// const FloatingSaveButton = ({
//   viewMode,
//   onToggle,
//   generatedCount,
//   savedCount,
// }) => {
//   const { isOver, setNodeRef } = useDroppable({ id: 'floatingSave' });

//   const isGenerated = viewMode === 'generated';

//   // If we are in "generated" mode, we show a Save icon + the savedCount
//   // If we are in "saved" mode, we show a SmartToy icon + the generatedCount
//   const icon = isGenerated ? <SaveIcon /> : <SmartToyOutlinedIcon />;
//   const tooltip = isGenerated
//     ? 'Switch to Saved Actions'
//     : 'Switch to Generate Actions';

//   const badgeNumber = isGenerated ? savedCount : generatedCount;

//   return (
//     <Tooltip title={tooltip} arrow placement="left">
//       <Badge
//         ref={setNodeRef}
//         badgeContent={badgeNumber}
//         color="primary"
//         max={99}
//         overlap="circular"
//         sx={{
//           '& .MuiBadge-badge': {
//             fontSize: '0.75rem',
//             height: '22px',
//             minWidth: '22px',
//             padding: '0 4px',
//             top: '0px',
//             right: '-5px',
//           },
//         }}
//       >
//         <IconButton
//           onClick={onToggle}
//           sx={{
//             backgroundColor: isOver ? 'primary.light' : 'background.paper',
//             boxShadow: 3,
//             '&:hover': {
//               backgroundColor: 'action.hover',
//               transform: 'scale(1.1)',
//             },
//             transition: 'transform 0.2s ease-in-out',
//           }}
//         >
//           {icon}
//         </IconButton>
//       </Badge>
//     </Tooltip>
//   );
// };

// /** =========================
//  *   Trash Modal
//  ========================= **/
// const TrashModal = ({ open, onClose, discardedControls, onRestore }) => (
//   <Dialog
//     open={open}
//     onClose={onClose}
//     maxWidth="sm"
//     fullWidth
//     TransitionComponent={Fade}
//     transitionDuration={300}
//   >
//     <DialogTitle
//       sx={{
//         display: 'flex',
//         alignItems: 'center',
//         justifyContent: 'space-between',
//         borderBottom: 1,
//         borderColor: 'divider',
//         pb: 2,
//       }}
//     >
//       <Box display="flex" alignItems="center" gap={1}>
//         <DeleteIcon color="error" />
//         <Typography variant="h6">Discarded Controls</Typography>
//       </Box>
//       <IconButton onClick={onClose} size="small">
//         <CloseIcon />
//       </IconButton>
//     </DialogTitle>

//     <DialogContent sx={{ mt: 2 }}>
//       {discardedControls.length > 0 ? (
//         <List>
//           {discardedControls.map((control, index) => (
//             <ListItem
//               key={control.controlId || index}
//               sx={{
//                 borderRadius: 1,
//                 mb: 1,
//                 '&:hover': {
//                   backgroundColor: 'action.hover',
//                 },
//               }}
//             >
//               <ListItemText
//                 primary={control.name}
//                 secondary={`Risk: ${control.riskName}`}
//                 primaryTypographyProps={{
//                   variant: 'body1',
//                   fontWeight: 500,
//                 }}
//                 secondaryTypographyProps={{
//                   variant: 'body2',
//                   color: 'text.secondary',
//                 }}
//               />
//               <ListItemSecondaryAction>
//                 <Tooltip title="Restore control" arrow>
//                   <IconButton
//                     edge="end"
//                     onClick={() => onRestore(control)}
//                     size="small"
//                     sx={{
//                       '&:hover': {
//                         color: 'primary.main',
//                       },
//                     }}
//                   >
//                     <RestoreIcon />
//                   </IconButton>
//                 </Tooltip>
//               </ListItemSecondaryAction>
//             </ListItem>
//           ))}
//         </List>
//       ) : (
//         <Box
//           display="flex"
//           alignItems="center"
//           justifyContent="center"
//           minHeight={200}
//           flexDirection="column"
//           gap={2}
//         >
//           <DeleteIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
//           <Typography color="text.secondary" variant="body1">
//             No discarded controls
//           </Typography>
//         </Box>
//       )}
//     </DialogContent>

//     <DialogActions
//       sx={{
//         borderTop: 1,
//         borderColor: 'divider',
//         px: 3,
//         py: 2,
//       }}
//     >
//       <Button onClick={onClose} variant="outlined" startIcon={<CloseIcon />}>
//         Close
//       </Button>
//     </DialogActions>
//   </Dialog>
// );

// /** =========================
//  *   DraggableControl
//  ========================= **/
// const DraggableControl = ({
//   control,
//   isSavedControl,
//   handleConfirmEdit,
//   handleDiscardControl,
//   onSaveControlClick,
//   modelId,
//   assessmentUuid,
//   handleSaveOperation,
//   findRiskById,
//   riskId
// }) => {
//   const { attributes, listeners, setNodeRef, transform, transition } = useDraggable({
//     id: control.name,
//   });

//   const style = {
//     transform: CSS.Translate.toString(transform),
//     transition,
//     cursor: 'grab',
//   };

//   const [isEditing, setIsEditing] = useState(false);
//   const [localText, setLocalText] = useState(control.name);
//   const [drawerOpen, setDrawerOpen] = useState(false);

//   useEffect(() => {
//     if (!isEditing) {
//       setLocalText(control.name);
//     }
//   }, [control.name, isEditing]);

//   function handleEdit() {
//     setIsEditing(true);
//   }
//   function handleChange(e) {
//     setLocalText(e.target.value);
//   }
//   async function handleCheck() {
//     await handleConfirmEdit(control, localText, isSavedControl);
//     setIsEditing(false);
//   }
//   const handleEvaluateClick = () => {
//     if (!isSavedControl) return;
//     setDrawerOpen(true);
//   };
//   const handleDrawerClose = () => {
//     setDrawerOpen(false);
//   };

//   const evaluationData = useSelector(state =>
//     state.controls.savedControlEvaluations[riskId]?.[control.controlId]?.[0] || {}
//   );
  
//   const hasEvaluationData =
//     isSavedControl &&
//     (evaluationData.control_recurrence || evaluationData.executed_next || evaluationData.self_evaluation);
    
//     console.log ('riskId extracted from control', riskId)
//   return (
//     <>
//       <TableRow ref={setNodeRef} style={style} {...attributes} {...listeners}>
//             <TableCell>
//           {hasEvaluationData && (
//             <CheckCircleIcon color="success" fontSize="small" />
//           )}
//         </TableCell>

//       {/* 2. Action Name */}
//       <TableCell>
//         {isEditing ? (
//           <TextField
//             value={localText}
//             onChange={handleChange}
//             fullWidth
//             variant="outlined"
//             size="small"
//           />
//         ) : (
//           <Typography sx={{ cursor: 'pointer', flexGrow: 1 }}>
//             {control.name}
//           </Typography>
//         )}
//       </TableCell>
//       <TableCell>
//           <Chip
//             label={control.controlModelStage || '–'}
//             size="small"
//           />
//         </TableCell>

//             <TableCell>
//               {hasEvaluationData ? evaluationData.control_recurrence : '-'}
//             </TableCell>
//             <TableCell>
//               {hasEvaluationData ? evaluationData.executed_next : '-'}
//             </TableCell>
//             <TableCell>
//               {hasEvaluationData ? evaluationData.self_evaluation : '-'}
//             </TableCell>
//             <TableCell>
//               <Box display="flex" alignItems="center" gap={1}>
//                 {isEditing ? (
//                   <IconButton onClick={handleCheck}>
//                     <CheckIcon />
//                   </IconButton>
//                 ) : (
//                   <>
//                     <IconButton onClick={handleEdit}>
//                       <EditIcon />
//                     </IconButton>
//                     {isSavedControl && (
//                     <Tooltip title={hasEvaluationData ? 'Re-Evaluate' : 'Action details'} arrow>
//                     <IconButton onClick={handleEvaluateClick}>
//                       {hasEvaluationData ? <TuneSharpIcon /> : <ListAltOutlinedIcon />}
//                     </IconButton>
//                   </Tooltip>
//                     )}
//                     <IconButton onClick={() => handleDiscardControl(findRiskById(riskId), control)}>
//                       <DeleteIcon style={{ color: 'red' }} />
//                     </IconButton>
//                     {!isSavedControl && (
//                       <IconButton
//                         onClick={() =>
//                           onSaveControlClick(
//                             { riskId, name: control.riskName },
//                             control
//                           )
//                         }
//                       >
//                         <SaveIcon />
//                       </IconButton>
//                     )}
//                   </>
//                 )}
//               </Box>
//             </TableCell>
//           </TableRow>

//       {/* Drawer for Control Evaluation Form */}
      
//       <Drawer
//         anchor="right"
//         open={drawerOpen}
//         onClose={handleDrawerClose}
//         transitionDuration={{ enter: 300, exit: 300 }}
//         PaperProps={{
//           sx: {
//             width: { xs: '100%', sm: '80%', md: '60%', lg: '50%' }, // Responsive widths
//             padding: 2,
//             display: 'flex',
//             flexDirection: 'column',
//             height: '100%',
//           },
//         }}
//         aria-labelledby="action-details-title"
//       >
//         {/* Drawer Header */}
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
//           <Typography id="action-details-title" variant="h6">Action Details</Typography>
//           <IconButton onClick={handleDrawerClose} aria-label="Close drawer" sx={{ color: 'grey.600', '&:hover': { color: 'primary.main' } }}>
//             <CloseIcon />
//           </IconButton>
//         </Box>
//         <Divider />
//         {/* Scrollable Content */}

       
//         <Box sx={{ flexGrow: 1, overflowY: 'auto', mt: 2 }}>
//           <ControlEvaluationForm
//             riskId={riskId}
//             controlId={control.controlId}
//             modelId={modelId}
//             assessmentUuid={assessmentUuid}
//             handleSaveOperation={handleSaveOperation}
//           />
//         </Box>
//         {/* Drawer Footer */}
//         <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
//           <Button variant="contained" onClick={handleDrawerClose} startIcon={<CloseIcon />}>
//             Close
//           </Button>
//         </Box>
//       </Drawer>
//     </>
//   );
// };




// function ActionPlan({ riskFormData, modelId, assessmentUuid, sectionName, narratives }) {
//   const dispatch = useDispatch();

//   // --- Redux State ---
//   const controls = useSelector((state) => state.controls.controls || {}); // Case-specific
//   const riskAreaData = riskFormData[sectionName];
//   const isLoading = useSelector((state) => state.controls.loading);
//   const allSaved = useSelector(s => s.controls.savedControls || {});
//   const savedControls = useMemo(() => {
//   const filtered = {};
//   Object.entries(allSaved).forEach(([rid, arr]) => {
//     const mine = arr.filter(c => c.modelId === modelId);
//     if (mine.length) filtered[rid] = mine;
//   });
//   return filtered;
// }, [allSaved, modelId]);
//   /* -------- discarded controls (filter by current model) -------- */
//   const allDiscardedObj = useSelector((s) => s.controls.discardedControls || {});

//   const discardedControlsObj = useMemo(() => {
//     const filtered = {};
//     Object.entries(allDiscardedObj).forEach(([rid, arr]) => {
//       const mine = arr.filter((c) => c.modelId === modelId);
//       if (mine.length) filtered[rid] = mine;
//     });
//     return filtered;
//   }, [allDiscardedObj, modelId]);

//   const discardedControls = useMemo(
//     () => Object.values(discardedControlsObj).flat(),
//     [discardedControlsObj]
//   );
//   const savedControlEvaluations = useSelector(
//     (state) => state.controls.savedControlEvaluations || {}
//   );

//   const sectionRisks = useSelector(
//     (state) => state.risks.models[modelId]?.riskFormData[sectionName]?.sectionRisks || []
//   );
//   const controlModelType  = useSelector(state => state.risks.models[modelId]?.modelType);
//   const controlModelStage = useSelector(state => state.risks.models[modelId]?.modelStage);
//   const actionPlan = useSelector(s=>s.controls.actionPlan||[]);
//   // --- Local State ---
//   const [expandedRiskCard, setExpandedRiskCard] = useState(null);
//   const [expandedControlEvaluation, setExpandedControlEvaluation] = useState(null); 
//   const [showTrashModal, setShowTrashModal] = useState(false);
//   const [viewMode, setViewMode] = useState('saved');
//   const [byRisk, setByRisk] = useState(false); 
//   const [editingRowId, setEditingRowId] = useState(null);
//   const [editingText,  setEditingText]  = useState('');
//   // Snackbar
//   const [snackbarOpen, setSnackbarOpen] = useState(false);
//   const [snackbarMessage, setSnackbarMessage] = useState('');
//   const [snackbarSeverity, setSnackbarSeverity] = useState('info');
//   const head = { backgroundColor:'#F9FAFB', color:'#6B7280', fontWeight:500 };
//   // DnD
//   const [activeControl, setActiveControl] = useState(null);
//   const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

//   // Hooks
//   const { isSaving, isSaved, handleSaveOperation } = useSaveOperation();

//   // Flattened "Saved" risks for referencing
//   // (removed: flattenedRisks)
  

//   // ─── Memoized counts ─────────────────────────────────────────────
//   const { generatedCountGlobal, savedCountGlobal, trashCountGlobal, savedSetGlobal } = useMemo(() => {
//     const savedSetGlobal = new Set(
//       Object.values(savedControls).flat().map(c => c.controlId)
//     );
//     const generatedCountGlobal = actionPlan.filter(c => c.status === 'generated' && c.modelId === modelId).length;
//     const savedCountGlobal     = actionPlan.filter(c => c.status === 'saved' && c.modelId === modelId).length;
//     const trashCountGlobal     = new Set(discardedControls.map(c => c.controlId)).size;
//     return { generatedCountGlobal, savedCountGlobal, trashCountGlobal, savedSetGlobal };
//   }, [actionPlan, savedControls, discardedControls, modelId]);

//   // ─── Rows for By-Action view ───────────────────────────────────────
//   const rows = useMemo(() => {
//     // Only include controls for the current model and selected view mode
//     return actionPlan
//       .filter(c => c.modelId === modelId)
//       .filter(c => c.status === viewMode);
//   }, [actionPlan, viewMode, modelId]);

//   // On mount => fetch saved controls, etc.
//   useEffect(() => {
//     if (modelId && assessmentUuid) {
//       dispatch(setSavedControls(modelId, assessmentUuid));
//       dispatch(fetchSavedControlEvaluations(modelId, assessmentUuid));
//     }
//   }, [dispatch, modelId, assessmentUuid]);

//   function handleCardClick(riskId) {
//     const isExpanded = expandedRiskCard === riskId;
//     setExpandedRiskCard(isExpanded ? null : riskId);

//     if (!isExpanded) {
//       // We are expanding the risk. Check if it has saved controls:
//       const haveSaved = (savedControls[riskId]?.length || 0) > 0;
//       setViewMode(haveSaved ? 'saved' : 'generated');
//     }
//   }

//   function handleConfirmEdit(control, newName, isSavedControl) {
//     if (isSavedControl) {
//       // save to DB then dispatch
//       handleUpdateSavedControl(control, newName);
//       dispatch(editSavedActionPlanRow(control.controlId, newName));
//     } else {
//       // generated only
//       dispatch(editGeneratedControl(control.riskId, control.controlId, newName));
//       dispatch(editGeneratedActionPlanRow(control.controlId, newName));
//     }
//   }

//   async function handleUpdateSavedControl(control, newName) {
//     // 1) Construct an updated control
//     const updated = { ...control, name: newName };
//     // 2) DB update
//     try {
//       await handleSaveOperation(() =>
//         handleSaveControl(
//           { riskId: updated.riskId, name: updated.riskName },
//           updated
//         )
//       );
//       // 3) Optionally call something like "editSavedControl" in Redux
//       dispatch(editSavedControl(updated.riskId, updated.controlId, newName));

//       setSnackbarMessage('Saved action updated successfully!');
//       setSnackbarSeverity('success');
//       setSnackbarOpen(true);
//     } catch (err) {
//       setSnackbarMessage(err.message || 'Error updating saved control');
//       setSnackbarSeverity('error');
//       setSnackbarOpen(true);
//     }
//   }

//   // Toggle control evaluation
//   const handleControlEvaluationToggle = (controlId) => {
//     setExpandedControlEvaluation((prev) => (prev === controlId ? null : controlId));
//   };

//   /** ========== Drag/Drop Handlers ========== **/
//   const handleDragStart = ({ active }) => {
//     const foundControl = findControlByName(active.id);
//     setActiveControl(foundControl || null);
//   };

//   const handleDragEnd = ({ active, over }) => {
//     if (!over) {
//       setActiveControl(null);
//       return;
//     }
//     const overId = over.id;
//     const draggedControl = findControlByName(active.id);
//     if (!draggedControl) {
//       setSnackbarMessage('Control not found.');
//       setSnackbarSeverity('error');
//       setSnackbarOpen(true);
//       setActiveControl(null);
//       return;
//     }

//     // If user drops onto the floating save icon:
//     if (overId === 'floatingSave') {
//       dispatch(addSavedControl(draggedControl.riskId, draggedControl));
//       setSnackbarMessage('Control saved successfully!');
//       setSnackbarSeverity('success');
//     } else if (overId === 'discardedControls') {
//       dispatch(discardControl(draggedControl, draggedControl.riskId));
//       setSnackbarMessage('Control discarded successfully!');
//       setSnackbarSeverity('info');
//     } else {
//       console.error('Invalid drop zone:', overId);
//       setSnackbarMessage('Invalid drop zone');
//       setSnackbarSeverity('error');
//     }

//     setSnackbarOpen(true);
//     setActiveControl(null);
//   };

//   const handleDragCancel = () => setActiveControl(null);

//   // Find a control by "name"
//   const findControlByName = (name) => {
//     const allGenerated = Object.values(controls).flat();
//     const allSaved     = Object.values(savedControls).flat();
//     return [...allGenerated, ...allSaved].find(c => c.name === name);
//   };

//   // Possibly find a risk by ID
//   const findRiskById = (riskId) => allRisks.find((r) => r.riskId === riskId);

//   /* ---- discard helpers stay separate & explicit ---- */
//   const handleDiscardGeneratedControl = (risk, control) => {
//     const rid = risk?.riskId || (Array.isArray(control.riskIds) ? control.riskIds[0] : undefined);
//     dispatch(discardControl(control, rid));
//     setSnackbarMessage('Generated control discarded');
//     setSnackbarSeverity('info');
//     setSnackbarOpen(true);
//   };

//   const handleDiscardSavedControl = (ctrl, rid) => {
//     dispatch(discardSavedControl(ctrl, rid));   // pass the whole control
//     setSnackbarMessage('Saved control discarded');
//     setSnackbarSeverity('info');
//     setSnackbarOpen(true);
//   };



//   const onSaveControlClick = async (risk, control) => {
//     try {
//       await handleSaveOperation(() => handleSaveControl(risk, control));
//       setSnackbarMessage('Control saved successfully!');
//       setSnackbarSeverity('success');
//     } catch (error) {
//       setSnackbarMessage(error.message || 'Error saving control');
//       setSnackbarSeverity('error');
//     } finally {
//       setSnackbarOpen(true);
//     }
//   };

//   const handleSaveControl = async (risk, control) => {
//     const { riskId } = risk;
//     const modelUuid = modelId;
//     const controlName = control.name;
//     const controlUuid = control.controlId;

//     try {
//       const response = await fetch('/.netlify/functions/saveControl', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({
//           modelUuid,
//           assessmentUuid,
//           //riskUuid: riskId,
//           riskIds: control.riskIds || [riskId],
//           controlName,
//           controlModelType,
//           controlModelStage,
//           ...(controlUuid ? { controlUuid } : {}),
//         }),
//       });
//       if (!response.ok) {
//         throw new Error('Failed to save the control');
//       }

//       const data = await response.json();

//       dispatch(
//         addSavedControl({          
//           ...control,               
//           riskIds: control.riskIds || [riskId] 
//         })
//       );
//       return data;
//     } catch (err) {
//       console.error('Error saving control:', err);
//       throw err;
//     }
//   };

//   /** ========== Generating/Fetching logic ========== **/
//   const handleGenerateRiskSpecificActions = (riskId) => {
//     const foundRisk = findRiskById(riskId);
//     if (!foundRisk) {
//       setSnackbarMessage('Risk not found.');
//       setSnackbarSeverity('error');
//       setSnackbarOpen(true);
//       return;
//     }
//     dispatch(
//       fetchControls({
//         sectionName,
//         narratives,
//         riskSourceData: riskAreaData,
//         sectionRisks: [foundRisk],
//         controlModelType,
//         controlModelStage,
//       })
//     );
//   };

//   // Snackbar close
//   const handleSnackbarClose = (_, reason) => {
//     if (reason === 'clickaway') return;
//     setSnackbarOpen(false);
//   };


//   return (
//     <Box sx={{ backgroundColor: '#F9FAFB', p: 3, minHeight: '100vh' }}>
//       <DndContext
//         sensors={sensors}
//         collisionDetection={closestCenter}
//         onDragStart={handleDragStart}
//         onDragEnd={handleDragEnd}
//         onDragCancel={handleDragCancel}
//         modifiers={[restrictToWindowEdges]}
//       >
//         {/* ───────── Header ───────── */}
//         <Box
//           sx={{
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'space-between',
//             mb: 3,
//             pb: 2,
//           }}
//         >
//           <Box sx={{ display: 'flex', alignItems: 'center' }}>
//             <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', ml: 1 }}>
//               Risk Handling Plan
//             </Typography>
//             <Box sx={{ ml: 1 }}>
//               <AutosaveIndicator isSaving={isSaving} isSaved={isSaved} />
//             </Box>
//           </Box>
  
//           {/* Switch + Generate */}
//           <FormControlLabel
//             control={
//               <Switch checked={byRisk} onChange={(e) => setByRisk(e.target.checked)} color="primary" />
//             }
//             label="By Risk"
//           />
//           {!byRisk && (
//             <Button
//               variant="outlined"
//               size="small"
//               startIcon={
//                 isLoading ? (
//                   <IconWithProgress isLoading tooltip="Generating action plan" />
//                 ) : (
//                   <SmartToyOutlinedIcon />
//                 )
//               }
//               disabled={isLoading}
//               onClick={() =>
//                 dispatch(
//                   fetchActionPlan({
//                     sectionName,
//                     riskSourceData: riskAreaData,
//                     sectionRisks: flattenedRisks,
//                     controlModelType,
//                     controlModelStage,
//                     modelId, 
//                     savedControls: Object.values(savedControls).flat(),
//                   })
//                 )
//               }
//               sx={{
//                 ml: 2,
//                 backgroundColor: '#111827',
//                 color: '#fff',
//                 textTransform: 'none',
//                 fontWeight: 600,
//                 borderRadius: 1.5,
//                 px: 2.5,
//                 py: 1,
//                 '&:hover': { backgroundColor: '#1F2937' },
//               }}
//             >
//               {generatedCountGlobal=== 0 ? 'Generate Action Plan' : 'Refresh Action Plan'}
//             </Button>
//           )}
//         </Box>
  
//         {/* ───────── Per-Risk table (shown when byRisk = true) ───────── */}
//         {byRisk && (
//           <Box sx={{ backgroundColor: '#F9FAFB', borderRadius: 1, border: '1px solid #E5E7EB', mb: 3 }}>
//             <TableContainer>
//               <Table sx={{ minWidth: 650 }}>
//                 <TableHead>
//                   <TableRow>
//                     <TableCell sx={head}>Risk Name</TableCell>
//                     <TableCell sx={head}>Evaluated Actions</TableCell>
//                     <TableCell sx={head}>Actions Generated</TableCell>
//                     <TableCell sx={head}>Expand</TableCell>
//                   </TableRow>
//                 </TableHead>
  
//                 <TableBody>
//                   {flattenedRisks.map((risk) => {
//                     /* per-risk counts */
//                     const generatedCountForRisk = (controls[risk.riskId] || []).length;
//                     const savedForRisk          = savedControls[risk.riskId] || [];
//                     const savedCountForRisk     = savedForRisk.length;
//                     const evaluatedCount        = savedForRisk.filter(ctrl => {
//                       const d = savedControlEvaluations[risk.riskId]?.[ctrl.controlId]?.[0] || {};
//                       return d.control_recurrence || d.executed_next || d.self_evaluation;
//                     }).length;
  
                  
//                       return (
//                         <React.Fragment key={risk.riskId}>
//                           <TableRow hover>
//                             <TableCell>{risk.riskName}</TableCell>
//                             <TableCell>{`${evaluatedCount}/${savedForRisk.length}`}</TableCell>
//                             <TableCell>
//                               {generatedCountForRisk + savedCountForRisk > 0
//                                 ? <CheckCircleIcon color="success" />
//                                 : <SmartToyOutlinedIcon color="disabled" />}
//                             </TableCell>
//                             <TableCell>
//                               <IconButton onClick={() => handleCardClick(risk.riskId)}>
//                                 {expandedRiskCard === risk.riskId
//                                   ? <ExpandLessIcon/>
//                                   : <ExpandMoreIcon/>}
//                               </IconButton>
//                             </TableCell>
//                           </TableRow>
  
//                         {/* ─ expanded card ─ */}
//                         {expandedRiskCard === risk.riskId && (
//                           <TableRow>
//                             <TableCell colSpan={4} sx={{ p: 0 }}>
//                               <Collapse in timeout="auto" unmountOnExit>
//                                 {/* floating buttons inside expanded card */}
//                                 <Box sx={{ p: 2 }}>
//                                   <Box
//                                     sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
//                                   >
//                                     <Typography variant="h6">
//                                       {viewMode === 'saved' ? 'Saved Actions' : 'Generated Actions'}
//                                     </Typography>
//                                     <Box sx={{ display: 'flex', gap: 2 }}>
//                                       <FloatingSaveButton
//                                         generatedCount={generatedCountGlobal}
//                                         savedCount={savedCountGlobal}
//                                         viewMode={viewMode}
//                                         onToggle={() => setViewMode((v) => (v === 'saved' ? 'generated' : 'saved'))}
//                                       />
//                                       <FloatingTrashButton
//                                         trashCount={trashCountGlobal}
//                                         onClick={() => setShowTrashModal(true)}
//                                       />
//                                     </Box>
//                                   </Box>
  
//                                   {/* risk-specific Generate / Refresh */}
//                                   {viewMode === 'generated' && (
//                                     <Button
//                                       variant="outlined"
//                                       size="small"
//                                       startIcon={
//                                         isLoading ? (
//                                           <IconWithProgress isLoading tooltip="Generating actions" />
//                                         ) : (
//                                           <SmartToyOutlinedIcon />
//                                         )
//                                       }
//                                       disabled={isLoading}
//                                       onClick={() => handleGenerateRiskSpecificActions(risk.riskId)}
//                                       sx={{
//                                         mb: 2,
//                                         backgroundColor: '#111827',
//                                         color: '#fff',
//                                         textTransform: 'none',
//                                         fontWeight: 600,
//                                         borderRadius: 1.5,
//                                         px: 2.5,
//                                         py: 1,
//                                         '&:hover': { backgroundColor: '#1F2937' },
//                                       }}
//                                     >
//                                       {generatedCountGlobal === 0 ? 'Generate Action Plan' : 'Refresh Action Plan'}
//                                     </Button>
//                                   )}
  
//                                   {/* inner white card with DraggableControl rows */}
//                                   <Box sx={{ backgroundColor: '#fff', borderRadius: 1, overflow: 'hidden', boxShadow: 1 }}>
//                                     <TableContainer sx={{ overflowX: 'auto' }}>
//                                       <Table size="small" sx={{ width: '100%' }}>
//                                         <TableHead>
//                                           <TableRow>
//                                             <TableCell sx={head}>Evaluated?</TableCell>
//                                             <TableCell sx={head}>Action Name</TableCell>
//                                             <TableCell sx={head}>Stage</TableCell>
//                                             <TableCell sx={head}>Recurrence</TableCell>
//                                             <TableCell sx={head}>Next Executed</TableCell>
//                                             <TableCell sx={head}>Self Evaluation</TableCell>
//                                             <TableCell sx={head}>Actions</TableCell>
//                                           </TableRow>
//                                         </TableHead>

//                                         <TableBody>
//                                           {(viewMode === 'saved'
//                                             ? savedControls[risk.riskId]
//                                             : controls[risk.riskId]
//                                           )?.map((ctrl) => (
//                                             <DraggableControl
//                                               key={ctrl.controlId}
//                                               control={ctrl}
//                                               riskId={risk.riskId}
//                                               isSavedControl={viewMode === 'saved'}
//                                               handleConfirmEdit={handleConfirmEdit}
//                                               handleDiscardControl={viewMode==='saved'
//                                                 ? handleDiscardSavedControl
//                                                 : handleDiscardGeneratedControl}
//                                               onSaveControlClick={onSaveControlClick}
//                                               handleControlEvaluationToggle={handleControlEvaluationToggle}
//                                               modelId={modelId}
//                                               assessmentUuid={assessmentUuid}
//                                               expandedControlEvaluation={expandedControlEvaluation}
//                                               handleSaveOperation={handleSaveOperation}
//                                               findRiskById={findRiskById}
//                                             />
//                                           ))}

//                                           {/* empty-state row */}
//                                           {(
//                                             (viewMode === 'saved'
//                                               ? savedControls[risk.riskId]
//                                               : controls[risk.riskId]
//                                             )?.length ?? 0
//                                           ) === 0 && (
//                                             <TableRow>
//                                               <TableCell colSpan={7} align="center">
//                                                 <Typography>
//                                                   {viewMode === 'saved'
//                                                     ? 'No saved actions.'
//                                                     : 'No actions generated yet – use the button above to generate.'}
//                                                 </Typography>
//                                               </TableCell>
//                                             </TableRow>
//                                           )}
//                                         </TableBody>
//                                       </Table>
//                                     </TableContainer>
//                                   </Box>
//                                 </Box>
//                               </Collapse>
//                             </TableCell>
//                           </TableRow>
//                         )}
//                       </React.Fragment>
//                     );
//                   })}
//                 </TableBody>
//               </Table>
//             </TableContainer>
//           </Box>
//         )}
  
//         {/* ───────── By-Action flat table (shown when byRisk = false) ───────── */}
//         {!byRisk && (
//           <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: 1, p: 2, mb: 3 }}>
//             {/* floating buttons */}
//             <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
//             <FloatingSaveButton
//               generatedCount={generatedCountGlobal}
//               savedCount={savedCountGlobal}
//               viewMode={viewMode}
//               onToggle={() => setViewMode(v => (v === 'saved' ? 'generated' : 'saved'))}
//             />
//             <FloatingTrashButton
//               trashCount={trashCountGlobal}
//               onClick={() => setShowTrashModal(true)}
//             />
//           </Box>

//             <Table size="small">
//               <TableHead>
//                 <TableRow>
//                   <TableCell sx={head}>Evaluated?</TableCell>
//                   <TableCell sx={head}>Action</TableCell>
//                   <TableCell sx={head}>Mitigates Risks</TableCell>
//                   <TableCell sx={head}>Actions</TableCell>
//                 </TableRow>
//               </TableHead>

//               <TableBody>
//                 {rows.length === 0 ? (
//                   <TableRow>
//                     <TableCell colSpan={4} align="center">
//                       <Typography>
//                         {viewMode === 'saved'
//                           ? 'No saved actions.'
//                           : 'No actions generated yet, click Generate Action Plan.'}
//                       </Typography>
//                     </TableCell>
//                   </TableRow>
//                 ) : (
//                 rows.map(ctrl => {
//                   // Safeguards: riskIds may be missing on legacy rows
//                   const primaryRiskId = Array.isArray(ctrl.riskIds) && ctrl.riskIds.length
//                     ? ctrl.riskIds[0]
//                     : null;

//                   const evalRow = primaryRiskId
//                     ? (savedControlEvaluations[primaryRiskId]?.[ctrl.controlId]?.[0] || {})
//                     : {};

//                   const evaluated = evalRow.control_recurrence
//                     || evalRow.executed_next
//                     || evalRow.self_evaluation;

//                   const isSaved = savedSetGlobal.has(ctrl.controlId);

//                     return (
//                       <TableRow key={ctrl.controlId} hover>
//                         <TableCell>
//                           {evaluated ? <CheckCircleIcon color="success" fontSize="small" /> : '—'}
//                         </TableCell>
//                         <TableCell sx={{ maxWidth: 500 }}>
//                           {editingRowId === ctrl.controlId ? (
//                             <TextField
//                               value={editingText}
//                               onChange={e => setEditingText(e.target.value)}
//                               size="small"
//                               fullWidth
//                               autoFocus
//                             />
//                           ) : (
//                             ctrl.name
//                           )}
//                         </TableCell>
//                         <TableCell sx={{ maxWidth: 600 }}>
//                         {(ctrl.riskIds || []).map((rid) => {
//                             const r = allRisks.find((x) => x.riskId === rid);
//                             return (
//                               <Typography
//                                 key={rid}
//                                 component="span"
//                                 variant="body2"
//                                 sx={{
//                                   display: 'inline-block',
//                                   background: '#F3F4F6',
//                                   borderRadius: 1,
//                                   px: 1,
//                                   py: 0.25,
//                                   mr: 0.5,
//                                   mb: 0.5,
//                                   whiteSpace: 'normal'
//                                 }}
//                               >
//                                 {r ? r.riskName : rid}
//                               </Typography>
//                             );
//                           })}
//                         </TableCell>
//                         <TableCell>
//                             {editingRowId === ctrl.controlId ? (
//                                     <>
//                                       <IconButton
//                                         onClick={() => {
//                                           handleConfirmEdit(ctrl, editingText.trim(), isSaved);
//                                           setEditingRowId(null);
//                                         }}
//                                       >
//                                         <CheckIcon />
//                                       </IconButton>
//                                       <IconButton onClick={() => setEditingRowId(null)}>
//                                         <CloseIcon />
//                                       </IconButton>
//                                     </>
//                                   ) : (
//                                     <IconButton
//                                       onClick={() => {
//                                         setEditingRowId(ctrl.controlId);
//                                         setEditingText(ctrl.name);
//                                       }}
//                                     >
//                                       <EditIcon />
//                                     </IconButton>
//                                   )}

//                           {/* Delete or Save */}
//                           {isSaved ? (
//                             /* Saved row – one dispatch updates badge correctly */
//                             <IconButton
//                               onClick={() =>
//                                 handleDiscardSavedControl(ctrl, ctrl.riskIds[0])
//                               }
//                             >
//                              <DeleteIcon sx={{ color: 'red' }} />
//                             </IconButton>
//                           ) : (
//                             /* Generated row: show both Discard and Save */
//                             <>
//                               <IconButton
//                                 onClick={() => {
//                                   ctrl.riskIds.forEach(rid => {
//                                     handleDiscardGeneratedControl(findRiskById(rid), ctrl);
//                                   });
//                                 }}
//                               >
//                                 <DeleteIcon sx={{ color: 'red' }} />
//                               </IconButton>
//                               <IconButton
//                                 onClick={() =>
//                                   onSaveControlClick({ riskIds: ctrl.riskIds }, ctrl)
//                                 }
//                               >
//                                 <SaveIcon />
//                               </IconButton>
//                             </>
//                           )}
//                         </TableCell>
//                       </TableRow>
//                     );
//                   })
//                 )}
//               </TableBody>
//             </Table>
//           </Box>
//         )}
//         {/* Drag overlay */}
//         <DragOverlay>
//           {activeControl && (
//             <Box sx={{ px: 2, py: 1, backgroundColor: '#fff', boxShadow: 4, borderRadius: 1, opacity: 0.9 }}>
//               {activeControl.name}
//             </Box>
//           )}
//         </DragOverlay>
  
//         {/* Trash modal and snackbar (unchanged) */}
//         <TrashModal
//           open={showTrashModal}
//           onClose={() => setShowTrashModal(false)}
//           discardedControls={discardedControls}
//           onRestore={(ctrl) => onSaveControlClick({ riskId: ctrl.riskId }, ctrl)}
//         />
  
//         <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
//           <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
//             {snackbarMessage}
//           </Alert>
//         </Snackbar>
//       </DndContext>
//     </Box>
//   );
// }
//   export default ActionPlan;
